import { List, TextField, DateField, NumberField, useDataProvider, TopToolbar } from 'react-admin';
import Datagrid from '../Commons/Datagrid';
import PostPagination from '../Commons/PostPagination';
import Button from '@mui/material/Button';
import { useMediaQuery, createTheme, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import historyTracker from '../Commons/historyTracker';
import AuthNotification from '../Commons/AuthNotification';
import { StyleTheme, Style } from '../Commons/Styles.jsx';
import { createStyles, makeStyles } from '@mui/styles';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Mismo patron que InvoiceListActions en ProviderInvoicesList.jsx: un TopToolbar propio
// que navega con history.push (state + query) para llevar la factura (corre_facturacion)
// al formulario de creacion, en vez del href plano "#/{resource}/create" del ListActions
// compartido.
const AccountingExpenseListActions = (props) => {
  const { create, onCreate } = props;
  return (
    <TopToolbar>
      {create ? (
        <Button onClick={onCreate} color="primary" style={Style.ToolBarButton}>
          Crear
        </Button>
      ) : null}
    </TopToolbar>
  );
};

// El filtro ya viaja codificado en la URL como ?filter={"invoice":"..."} (asi es como se
// abre esta pantalla desde el boton "Gastos Contables" de ProviderInvoiceShow), asi que se
// reutiliza como fuente de verdad para saber a que factura esta acotado el listado.
// IMPORTANTE: no se puede confiar en location.state aqui, por la misma razon documentada
// en ProviderInvoicesList.jsx (<List syncWithLocation> reescribe la URL apenas monta y esa
// reescritura pierde el location.state original de history.push).
const getInvoiceFromFilterQuery = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const filterQuery = params.get('filter');
    if (!filterQuery) return null;
    const parsed = JSON.parse(filterQuery);
    return parsed?.invoice ?? null;
  } catch {
    return null;
  }
};

const getInvoiceNumberFromQuery = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    return params.get('invoiceNumber') || null;
  } catch {
    return null;
  }
};

// Por defecto, cuando el listado devuelve 0 filas, react-admin reemplaza TODO el
// <Datagrid> (encabezado incluido) por un mensaje generico tipo "No Provider/
// accountingexpenses found using the current filters." (ver ListNoResults en
// ra-ui-materialui). Para esta pantalla se prefiere seguir mostrando el encabezado de
// columnas normal aunque no haya gastos contables, asi que se arma un reemplazo propio
// (mismos encabezados que la tabla real) para el prop "empty" del Datagrid.
const AccountingExpensesEmpty = () => (
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell style={Style.DataGrid}>Centro</TableCell>
                <TableCell style={Style.DataGrid}>Cuenta</TableCell>
                <TableCell style={Style.DataGrid}>Monto</TableCell>
                <TableCell style={Style.DataGrid}>Fecha</TableCell>
                <TableCell style={Style.DataGrid}>Usuario</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            <TableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                    No hay gastos contables registrados para esta factura.
                </TableCell>
            </TableRow>
        </TableBody>
    </Table>
);

// Listado de gastos contables de una factura puntual (sp_lista_cbfacturaintegcontable
// acotado por corre_facturacion). Se llega aqui desde el boton "Gastos Contables" de
// ProviderInvoiceShow. El breadcrumb usa una etiqueta fija ("Gastos Contables"): al ser
// un listado, no un registro puntual, no tiene sentido sufijarlo con el numero de factura.
// invoiceNumber/invoiceId se mantienen igual, ya que los sigue necesitando
// handleCreateExpense para precargar el formulario de creacion.
const useStyles = makeStyles(() => createStyles(StyleTheme));

const AccountingExpensesList = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openAuth, setOpenAuth] = useState(false);
  const [auth, setAuth] = useState(false);
  const [authCreate, setAuthCreate] = useState(false);
  // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
  // "initialLoading" en el return): esta pantalla necesita traer permisos antes de poder
  // mostrar el listado, y sin este estado la ventana se veia en blanco (solo el
  // breadcrumb) durante esa espera.
  const [initialLoading, setInitialLoading] = useState(true);
  const dataProvider = useDataProvider();
  const invoiceNumber = getInvoiceNumberFromQuery(location.search) || location.state?.invoiceNumber;
  const invoiceId = getInvoiceFromFilterQuery(location.search) || location.state?.invoiceId;

  useEffect(() => {
    (async () => {
      try {
        const currentPath = location.pathname;
        historyTracker.addPath(currentPath, 'Gastos Contables', 6);

        const authdata = { data: hasPermissions('AccountingExpense', 'list,create') };
        if (authdata.data.length > 0) {
          setAuth(authdata.data[0]);
          setOpenAuth(!authdata.data[0]);
          setAuthCreate(authdata.data[1]);
        } else {
          setAuth(false);
          setOpenAuth(true)
        }
      }
      catch {
      }
      finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, location.pathname]);

  const handleCreateExpense = () => {
    navigate({
      pathname: '/provider/accountingexpense/create',
      search: `?invoice=${encodeURIComponent(invoiceId)}&invoiceNumber=${encodeURIComponent(invoiceNumber || '')}`,
    }, { state: { invoiceNumber, invoiceId } });
  };

  const handleRedirectProviderList = () => {
    navigate(`/provider`);
  };

  const [openProviders, setOpenProviders] = useState(true);

  const handleClickOpenProviders = () => {
    setOpenProviders(!openProviders);
  };

  const menus = [{
    icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
    open: openProviders,
    openAction: handleClickOpenProviders,
    actions: [
      { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
    ]
  }];

  let pagination = <PostPagination menu={<InternalMenu menus={menus} />} />;

  return (
    <div class="container">
      <BreadCrumbCustom history={historyTracker.getHistory()} />
      {initialLoading ? (
        // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y el
        // mismo CircularProgress que se usa en otras pantallas para la espera de guardado,
        // pero aqui cubre la espera de auth, que antes dejaba la pantalla en blanco.
        <div class="Loading" style={{ display: 'block' }}>
          <CircularProgress size="15rem" />
        </div>
      ) : auth ? (
        <div class="row">
          <div class="col-12">
            {isSmall ? (
              <List empty={false} {...props} actions={<AccountingExpenseListActions create={authCreate} onCreate={handleCreateExpense} />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props}>
                </Datagrid>
              </List>
            ) : (
              <List empty={false} perPage={10} {...props} actions={<AccountingExpenseListActions create={authCreate} onCreate={handleCreateExpense} />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} rowClick="show" style={Style.DataGrid} empty={<AccountingExpensesEmpty />}>
                  <TextField source="costCenterName" style={Style.fieldList} label="Centro" cellClassName={classes.fields} />
                  <TextField source="accountName" style={Style.fieldList} label="Cuenta" cellClassName={classes.fields} />
                  <NumberField source="amount" style={Style.fieldList} label="Monto" cellClassName={classes.fields} options={{ style: 'currency', currency: 'GTQ' }} />
                  <DateField source="movementDate" style={Style.fieldList} label="Fecha" cellClassName={classes.fields} />
                  <TextField source="user" style={Style.fieldList} label="Usuario" cellClassName={classes.fields} />
                </Datagrid>
              </List>
            )}
          </div>
        </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
    </div>
  )
};

export default AccountingExpensesList;
